/**
 * Recovering when code that loads on demand can no longer be fetched.
 *
 * Side panels, dialogs, pages and some libraries load the first time they are
 * used, from files named after the build. A deploy replaces those files, so a
 * tab opened before a deploy asks for files that are gone and the panel fails
 * to load. A download that failed because the connection dropped also stays
 * failed until the page reloads. Either way, the page reloads once the app's
 * server answers again. The app's leave check still asks first if edits could
 * be lost.
 */
import type { Router } from 'vue-router';
import { isServerReachable, whenOnline } from '@/helpers/lazyLoad';

const RELOADED_AT_KEY = 'stale-build-reloaded-at';
// failing again this soon after a reload means the build is not the cause
const RELOAD_WINDOW_MS = 60_000;
const SERVER_RETRY_MS = 5000;

/**
 * Reload whenever a lazy file fails to load. During a navigation it reloads
 * onto the page being opened, otherwise onto the current one.
 */
export function watchForStaleBuild(router: Router): void {
  let pendingPath: string | null = null;
  router.beforeEach(to => {
    pendingPath = to.fullPath;
  });
  router.afterEach(() => {
    pendingPath = null;
  });
  router.onError(error => {
    pendingPath = null;
    console.error(error);
  });

  // Vite fires this for any lazy file that fails, before the import rejects
  window.addEventListener('vite:preloadError', () => {
    const path =
      pendingPath ?? location.pathname + location.search + location.hash;
    void waitForServer().then(() => reloadOnce(path));
  });
}

/*
 * Helpers
 */

/** Resolves once this app's own server answers again. */
function waitForServer(): Promise<void> {
  return whenOnline()
    .then(isServerReachable)
    .then(reachable =>
      reachable
        ? undefined
        : new Promise<void>(resolve => {
            setTimeout(() => resolve(waitForServer()), SERVER_RETRY_MS);
          })
    );
}

function reloadOnce(path: string): void {
  try {
    const last = Number(sessionStorage.getItem(RELOADED_AT_KEY) ?? 0);
    if (Date.now() - last < RELOAD_WINDOW_MS) return;
    sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
  } catch {
    // without storage a reload could repeat forever, so leave the error showing
    return;
  }
  location.assign(path);
}
